import { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { DropdownMenuGroups } from '@/features/analytics/invite-links/ui/InviteLinksTable/DropdownMenuGroups'
import {
    InviteLinkWithAnalyticsType,
    useDeleteInviteLinksGroupChannelsMutation,
    useGetGroupsByChannelIdQuery,
} from '@/shared/api'
import { MagnifyingGlassSVGIcon } from '@/shared/assets'
import { SortHeader, Table } from '@/shared/components'
import { GreyRubleIconInline, RubleIconInline } from '@/shared/ui'
import { stopPropagation } from '@/true-fsd/shared/utils/react'

import { CpmCell } from '@/features/analytics/invite-links/ui/InviteLinksTable/shared/cells'
import { isMasterLink } from '@/features/analytics/invite-links/ui/InviteLinksTable/shared/masterLink'
import { useInviteLinksStore } from '@/features/analytics/model'
import {
    useActiveWorkspace,
    workspacePath,
} from '@/true-fsd/data/advertiser-account'
import { Checkbox2 } from '@/true-fsd/shared/ui/Checkbox2'
import { useMemo } from 'react'
import {
    GroupsChannelFormattedRowType,
    useGroupsChannelsFormattedRows,
} from '../hooks/useGroupsChannelsFormattedRows'
import { useGroupsSearch } from '../hooks/useGroupsSearch'
import { useGroupsSortedData } from '../hooks/useGroupsSortedData'

export const InviteLinksGroupsChannelsTable = ({
    className,
    channelId,
    handleCheckboxChange,
    isRowSelected,
}: {
    className?: string
    channelId: number
    handleCheckboxChange: (row: GroupsChannelFormattedRowType) => void
    isRowSelected: (row: GroupsChannelFormattedRowType) => boolean
}) => {
    const navigate = useNavigate()
    const { selectedGroups, clearSelectedGroupsByChannel } =
        useInviteLinksStore()
    const inviteLinkDeleteGroupMutation =
        useDeleteInviteLinksGroupChannelsMutation()
    const { movedGroups, clearMovedGroups } = useInviteLinksStore()

    const { data: groups = [], isLoading } = useGetGroupsByChannelIdQuery(
        channelId,
        {
            onSuccess: () => {
                clearMovedGroups()
                // Clear movedGroups only if the backend response indicates no pending moves
                // const formattedGroups = fetchedGroups.map((group) => ({
                //     groupId: group.invite_links_group_id,
                //     inviteLinkIds: group.inviteLinks.map((link) => link.invite_link_id),
                // }));
                // setMovedGroups(formattedGroups);
            },
            onError: () => {
                clearMovedGroups()
            },
        }
    )

    const rearrangedGroups = useMemo(() => {
        if (!groups.length) return []
        if (!movedGroups.length) return groups

        const movedGroupsMap = new Map<number, InviteLinkWithAnalyticsType[]>()
        movedGroups.forEach((group) => {
            movedGroupsMap.set(group.groupId, group.inviteLinks)
        })

        return groups.map((group) => {
            const groupId = group.invite_links_group_id
            const filteredInviteLinks = group.inviteLinks.filter((link) => {
                return !movedGroups.some((movedGroup) =>
                    movedGroup.inviteLinks.some(
                        (movedLink) =>
                            movedLink.invite_link_id === link.invite_link_id &&
                            movedGroup.groupId !== groupId
                    )
                )
            })
            const addedLinks = Array.from(movedGroupsMap.keys()).flatMap(
                (targetGroupId) => {
                    const linksMovedToThisGroup =
                        movedGroupsMap
                            .get(targetGroupId)
                            ?.filter(
                                (movedLink) =>
                                    targetGroupId === groupId &&
                                    !group.inviteLinks.some(
                                        (link) =>
                                            link.invite_link_id ===
                                            movedLink.invite_link_id
                                    )
                            ) || []

                    return linksMovedToThisGroup
                }
            )
            return {
                ...group,
                inviteLinks: [...filteredInviteLinks, ...addedLinks],
            }
        })
    }, [groups, movedGroups])

    const formattedRows = useGroupsChannelsFormattedRows(rearrangedGroups) // groups
    const { searchQuery, setSearchQuery, filteredData } =
        useGroupsSearch(formattedRows)
    const { sortedData, sortConfig, handleSort } =
        useGroupsSortedData(filteredData)

    useEffect(() => {
        return () => {
            if (channelId) {
                clearSelectedGroupsByChannel(channelId)
            }
        }
    }, [channelId, clearSelectedGroupsByChannel])

    const tableHead = (
        <tr>
            <th style={{ verticalAlign: 'middle', cursor: 'pointer' }}>
                <div className="flex h-full items-center justify-center">
                    <DropdownMenuGroups
                        selectedGroups={selectedGroups}
                        clearSelectedGroupsById={clearSelectedGroupsByChannel}
                        deleteMutation={inviteLinkDeleteGroupMutation}
                        workspaceId={channelId}
                    />
                </div>
            </th>
            <th>
                <div className="search-header">
                    <div className="search-input-wrapper">
                        <MagnifyingGlassSVGIcon className="search-header__icon" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Название"
                            className="search-input"
                        />
                    </div>
                    <SortHeader<GroupsChannelFormattedRowType>
                        title=""
                        field="groupName"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                    />
                </div>
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="Кол-во ссылок"
                    field="linksCount"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title={
                        <>
                            Бюджет <GreyRubleIconInline />
                        </>
                    }
                    field="budget"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="Дата создания"
                    field="creationDate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="Кол-во подписчиков"
                    field="subscribers"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="Охват"
                    field="reach"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="Вступлений"
                    field="subscriptions"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="CPM"
                    field="cpm"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="CPS"
                    field="cps"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="Отписки"
                    field="unsubscriptions"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="Отписки %"
                    field="unsubscriptionsPercentage"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsChannelFormattedRowType>
                    title="Итого CPS"
                    field="total_cps"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
        </tr>
    )

    const { activeWorkspace } = useActiveWorkspace()

    const tableBody = (
        <>
            {sortedData.map((row) => (
                <tr
                    key={row.id}
                    onClick={() =>
                        navigate(
                            workspacePath(
                                activeWorkspace,
                                `analytics/links/${row.id}`
                            )
                        )
                    }
                    className="group-name-link"
                >
                    <td>
                        <div className="flex h-full items-center justify-center">
                            <Checkbox2
                                checked={isRowSelected(row)}
                                onClick={stopPropagation}
                                onCheckedChange={() => {
                                    handleCheckboxChange(row)
                                }}
                                className="row-checkbox"
                            />
                        </div>
                    </td>
                    <td>
                        <span
                            className={
                                isMasterLink(row.groupName)
                                    ? '!font-semibold'
                                    : ''
                            }
                        >
                            {row.groupName}
                        </span>
                    </td>
                    <td>{row.linksCount}</td>
                    <td>
                        {row.budget} <RubleIconInline />
                    </td>
                    <td>
                        {new Date(row.creationDate).toLocaleDateString('ru-RU')}
                    </td>
                    <td>{row.subscribers}</td>
                    <td>{row.reach}</td>
                    <td>{row.subscriptions}</td>
                    <td>
                        <CpmCell>{row.cpm}</CpmCell>
                    </td>
                    <td>
                        {row.cps.toFixed(0)} <RubleIconInline />
                    </td>
                    <td>{row.unsubscriptions}</td>
                    <td>{row.unsubscriptionsPercentage.toFixed(2)}%</td>
                    <td>
                        {row.total_cps.toFixed(0)} <RubleIconInline />
                    </td>
                </tr>
            ))}
        </>
    )

    return (
        <Table
            isLoading={isLoading}
            thead={tableHead}
            tbody={tableBody}
            baseTableClassName={className}
            className={'groups-table'}
        />
    )
}
